import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import api from '../utils/api';
import { useLang } from '../contexts/LanguageContext';
import { UserPlus, X, Loader2, Check } from 'lucide-react';

/**
 * C4 - Bouton "Assigner ce ticket" pour les tickets non assignés.
 * Le modal est rendu via ReactDOM.createPortal dans document.body
 * pour échapper à tout stacking context / overflow parent.
 *
 * Props:
 *  - ticketId: string
 *  - onAssigned: (newAssignee) => void  (callback après succès)
 *  - size: 'sm' | 'md' (default 'md')
 *  - variant: 'primary' | 'inline' (default 'primary')
 */
export default function AssignButton({ ticketId, onAssigned, size = 'md', variant = 'primary' }) {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError('');
    api.get('/tickets/' + ticketId + '/assignable')
      .then(res => setUsers(res.data))
      .catch(() => setError(lang === 'fr' ? 'Impossible de charger la liste' : 'Failed to load list'))
      .finally(() => setLoading(false));
  }, [open, ticketId, lang]);

  const handleClose = useCallback((e) => {
    if (e) e.stopPropagation();
    setOpen(false);
  }, []);

  // Bloquer le scroll body + écouter Escape quand le modal est ouvert
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, handleClose]);

  function handleOpen(e) {
    e.stopPropagation();
    e.preventDefault();
    setOpen(true);
    setSelected('');
    setError('');
  }

  function roleLabel(role) {
    const labels = {
      member: { fr: 'Membre', en: 'Member' },
      consultant: { fr: 'Consultant', en: 'Consultant' },
      dr: { fr: 'DR', en: 'DR' },
      dn: { fr: 'DN', en: 'DN' },
      direction: { fr: 'Direction', en: 'Direction' },
      support: { fr: 'Support APOLLO', en: 'Support APOLLO' },
    };
    return (labels[role] || { fr: role, en: role })[lang] || role;
  }

  function handleConfirm(e) {
    e.stopPropagation();
    if (!selected) return;
    setSubmitting(true);
    setError('');
    api.put('/tickets/' + ticketId, { assigned_to: selected })
      .then(() => {
        const pick = users.find(u => u._id === selected);
        if (onAssigned) onAssigned(pick);
        setOpen(false);
      })
      .catch(err => {
        setError(err?.response?.data?.detail || (lang === 'fr' ? "Erreur lors de l'assignation" : 'Assignment failed'));
      })
      .finally(() => setSubmitting(false));
  }

  const btnClass = variant === 'inline'
    ? 'inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold transition-all'
    : 'inline-flex items-center gap-2 rounded-lg font-semibold transition-all ' + (size === 'sm' ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm');
  const btnStyle = { background: '#F77F00', color: '#fff' };

  const selectedUser = users.find(u => u._id === selected);
  const confirmLabel = selectedUser
    ? (lang === 'fr' ? 'Assigner à ' + selectedUser.name : 'Assign to ' + selectedUser.name)
    : (lang === 'fr' ? "Confirmer l'assignation" : 'Confirm assignment');

  const modal = open ? (
    <div
      data-testid="assign-modal-backdrop"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.65)',
        backdropFilter: 'blur(2px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        data-testid="assign-modal"
        onClick={e => e.stopPropagation()}
        className="animate-fade-in"
        style={{
          width: '100%',
          maxWidth: '480px',
          maxHeight: '90vh',
          background: '#1A1A2E',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '16px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        }}
      >
        {/* Header (sticky top) */}
        <div style={{ padding: '20px 20px 12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
          <h3 className="text-base font-bold text-white">
            {lang === 'fr' ? 'Assigner ce ticket' : 'Assign this ticket'}
          </h3>
          <button data-testid="assign-close" onClick={handleClose} aria-label="Fermer" className="p-1 rounded-lg hover:bg-white/5" style={{ color: '#9CA3AF' }}>
            <X size={20} />
          </button>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', minHeight: 0 }}>
          {loading ? (
            <div className="flex items-center justify-center py-8" style={{ color: '#9CA3AF' }}>
              <Loader2 size={20} className="animate-spin" />
            </div>
          ) : users.length === 0 ? (
            <p className="text-sm py-4 text-center" style={{ color: '#FBBF24' }}>
              {lang === 'fr' ? 'Aucun destinataire disponible pour ce ticket' : 'No assignee available for this ticket'}
            </p>
          ) : (
            <div className="space-y-1">
              {users.map(u => {
                const active = selected === u._id;
                return (
                  <button
                    key={u._id}
                    data-testid={'assign-option-' + u._id}
                    onClick={() => setSelected(u._id)}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-all"
                    style={{
                      background: active ? 'rgba(247,127,0,0.15)' : 'transparent',
                      border: '1px solid ' + (active ? '#F77F00' : 'rgba(255,255,255,0.06)'),
                    }}
                  >
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(247,127,0,0.15)' }}>
                      <span style={{ color: '#F77F00', fontWeight: 600, fontSize: 12 }}>{(u.name || '?').charAt(0)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{u.name}</p>
                      <p className="text-xs truncate" style={{ color: '#9CA3AF' }}>{roleLabel(u.role)} · {u.email}</p>
                    </div>
                    {active ? <Check size={16} style={{ color: '#F77F00', flexShrink: 0 }} /> : null}
                  </button>
                );
              })}
            </div>
          )}

          {error ? (
            <div data-testid="assign-error" role="alert" className="mt-3 p-2 rounded-lg text-xs" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444' }}>
              {error}
            </div>
          ) : null}
        </div>

        {/* Sticky footer with action buttons */}
        <div style={{ padding: '12px 20px 20px 20px', display: 'flex', gap: '8px', borderTop: '1px solid rgba(255,255,255,0.06)', background: '#1A1A2E', flexShrink: 0 }}>
          <button
            data-testid="assign-cancel"
            onClick={handleClose}
            className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold"
            style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: '#9CA3AF', minHeight: '44px' }}
          >
            {lang === 'fr' ? 'Annuler' : 'Cancel'}
          </button>
          <button
            data-testid="assign-confirm"
            onClick={handleConfirm}
            disabled={!selected || submitting}
            className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold truncate"
            style={{ background: '#F77F00', color: '#fff', opacity: !selected || submitting ? 0.5 : 1, minHeight: '44px' }}
          >
            {submitting ? (lang === 'fr' ? 'Assignation...' : 'Assigning...') : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <button
        data-testid={'assign-btn-' + ticketId}
        onClick={handleOpen}
        className={btnClass}
        style={btnStyle}
      >
        <UserPlus size={variant === 'inline' ? 12 : 14} />
        {lang === 'fr' ? 'Assigner' : 'Assign'}
      </button>
      {modal && typeof document !== 'undefined' ? ReactDOM.createPortal(modal, document.body) : null}
    </>
  );
}
